<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  sendingStartTime: {
    type: String,
    default: '',
  },
  sendingEndTime: {
    type: String,
    default: '',
  },
  pauseReason: {
    type: String,
    default: null,
  },
  sentCount: {
    type: Number,
    default: 0,
  },
  failedCount: {
    type: Number,
    default: 0,
  },
  totalContacts: {
    type: Number,
    default: 0,
  },
  audienceNames: {
    type: Array,
    default: () => [],
  },
  conversationsWithReplies: {
    type: Number,
    default: 0,
  },
  qualityRating: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit', 'delete', 'viewReport', 'resume', 'pause', 'restart']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_SENDING = 'sending';
const STATUS_PAUSED = 'paused';

const { formatMessage } = useMessageFormatter();

const isCompleted = computed(() => props.status === STATUS_COMPLETED);
const isSending = computed(() => props.status === STATUS_SENDING);

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : !isCompleted.value
);

const canResume = computed(() => !props.isLiveChatType && !isSending.value);
const canPause = computed(() => !props.isLiveChatType && isSending.value);
const canRestart = computed(() => !props.isLiveChatType);

const isPaused = computed(() => props.status === STATUS_PAUSED);

const statusTextColor = computed(() => {
  if (props.isLiveChatType) {
    return {
      'text-n-teal-11': isActive.value,
      'text-n-slate-12': !isActive.value,
    };
  }
  if (props.status === STATUS_SENDING) return { 'text-n-blue-11': true };
  if (props.status === STATUS_COMPLETED) return { 'text-n-slate-12': true };
  if (isPaused.value) return { 'text-n-amber-11': true };
  return { 'text-n-teal-11': true };
});

const statusBgColor = computed(() => {
  if (props.status === STATUS_SENDING) return 'bg-n-blue-3';
  if (props.status === STATUS_COMPLETED) return 'bg-n-alpha-2';
  if (isPaused.value) return 'bg-n-amber-3';
  return 'bg-n-alpha-2';
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isPaused.value) return 'Pausada';
  if (props.status === STATUS_SENDING) return 'Enviando';
  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const sendingWindow = computed(() => {
  if (!props.sendingStartTime && !props.sendingEndTime) return '';
  return `${props.sendingStartTime || '08:00'} - ${props.sendingEndTime || '18:00'}`;
});

const audienceLabel = computed(() => {
  if (!props.audienceNames || props.audienceNames.length === 0) return '';
  return props.audienceNames.join(', ');
});

const progressPercent = computed(() => {
  if (!props.totalContacts || props.totalContacts === 0) return 0;
  return Math.min(
    100,
    Math.round(((props.sentCount + props.failedCount) / props.totalContacts) * 100)
  );
});

const qualityLabel = computed(() => {
  const map = {
    GREEN: 'Alta',
    YELLOW: 'Média',
    RED: 'Baixa',
    UNKNOWN: 'Desconhecida',
  };
  return map[props.qualityRating] || props.qualityRating || '';
});

const qualityColor = computed(() => {
  const map = {
    GREEN: 'text-n-teal-11 bg-n-teal-3',
    YELLOW: 'text-n-amber-11 bg-n-amber-3',
    RED: 'text-n-ruby-11 bg-n-ruby-3',
  };
  return map[props.qualityRating] || 'text-n-slate-11 bg-n-alpha-2';
});

const qualityDot = computed(() => {
  const map = {
    GREEN: 'bg-n-teal-9',
    YELLOW: 'bg-n-amber-9',
    RED: 'bg-n-ruby-9',
  };
  return map[props.qualityRating] || 'bg-n-slate-9';
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <!-- Title + Status -->
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
          :class="[statusBgColor, statusTextColor]"
        >
          {{ campaignStatus }}
        </span>
      </div>

      <!-- Message preview with tooltip -->
      <div class="relative group w-full">
        <div
          v-dompurify-html="formatMessage(message, false, false, false)"
          class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6 cursor-default"
        />
        <div
          v-if="message"
          class="absolute left-0 top-full mt-1 z-50 hidden group-hover:block max-w-lg w-max p-3 rounded-lg shadow-lg border border-n-slate-6 bg-n-solid-2 text-sm text-n-slate-12"
        >
          <div
            v-dompurify-html="formatMessage(message, false, false, false)"
            class="[&>p]:mb-1 max-h-64 overflow-y-auto whitespace-pre-wrap"
          />
        </div>
      </div>

      <!-- Pause reason -->
      <div
        v-if="pauseReason"
        class="flex items-start gap-2 w-full p-2 rounded-lg bg-n-amber-3 border border-n-amber-6"
      >
        <span class="i-lucide-alert-triangle text-n-amber-11 shrink-0 mt-0.5" />
        <p class="text-xs text-n-amber-11 leading-relaxed line-clamp-2">
          {{ pauseReason }}
        </p>
      </div>

      <!-- Campaign details row -->
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <template v-else>
          <SMSCampaignDetails
            :inbox-name="inboxName"
            :inbox-icon="inboxIcon"
            :scheduled-at="scheduledAt"
          />
          <span
            v-if="sendingWindow"
            class="flex items-center gap-1 text-xs text-n-slate-10 flex-shrink-0"
          >
            <span class="i-lucide-clock text-xs" />
            {{ sendingWindow }}
          </span>
        </template>
      </div>

      <!-- Stats row (WhatsApp/SMS only) -->
      <div
        v-if="!isLiveChatType"
        class="flex flex-wrap items-center w-full gap-3 mt-1"
      >
        <!-- Contact list -->
        <span
          v-if="audienceLabel"
          class="flex items-center gap-1 text-xs text-n-slate-11"
          :title="audienceLabel"
        >
          <span class="i-lucide-list text-xs flex-shrink-0" />
          <span class="truncate max-w-[140px]">{{ audienceLabel }}</span>
        </span>

        <!-- Total contacts -->
        <span
          v-if="totalContacts > 0"
          class="flex items-center gap-1 text-xs text-n-slate-11"
          title="Total de contatos na lista"
        >
          <span class="i-lucide-users text-xs flex-shrink-0" />
          {{ totalContacts }} contatos
        </span>

        <!-- Sent count -->
        <span
          class="flex items-center gap-1 text-xs text-n-teal-11"
          title="Enviados com sucesso"
        >
          <span class="i-lucide-check-circle text-xs flex-shrink-0" />
          {{ sentCount }} enviados
        </span>

        <!-- Failed count -->
        <span
          v-if="failedCount > 0"
          class="flex items-center gap-1 text-xs text-n-ruby-11"
          title="Falhas no envio"
        >
          <span class="i-lucide-x-circle text-xs flex-shrink-0" />
          {{ failedCount }} falhas
        </span>

        <!-- Conversations with replies -->
        <span
          class="flex items-center gap-1 text-xs text-n-blue-11"
          title="Conversas que tiveram resposta"
        >
          <span class="i-lucide-message-circle text-xs flex-shrink-0" />
          {{ conversationsWithReplies }} respostas
        </span>

        <!-- Progress bar -->
        <div
          v-if="totalContacts > 0"
          class="flex items-center gap-1.5 flex-shrink-0"
          :title="`Progresso: ${progressPercent}%`"
        >
          <div class="w-16 h-1.5 bg-n-alpha-3 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all"
              :class="progressPercent === 100 ? 'bg-n-teal-9' : 'bg-n-blue-9'"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <span class="text-xs text-n-slate-10">{{ progressPercent }}%</span>
        </div>

        <!-- API Quality -->
        <span
          v-if="qualityRating"
          class="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
          :class="qualityColor"
          title="Qualidade da API WhatsApp"
        >
          <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="qualityDot" />
          Qualidade: {{ qualityLabel }}
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pencil"
        title="Editar campanha"
        @click="emit('edit')"
      />
      <Button
        v-if="canPause"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        title="Pausar envios"
        @click="emit('pause')"
      />
      <Button
        v-if="canResume"
        variant="faded"
        size="sm"
        color="blue"
        icon="i-lucide-play"
        title="Continuar envios (de onde parou)"
        @click="emit('resume')"
      />
      <Button
        v-if="canRestart"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-rotate-ccw"
        title="Reiniciar campanha do zero (limpa envios de hoje)"
        @click="emit('restart')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-bar"
        title="Ver histórico de envios"
        @click="emit('viewReport')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
